import { useMutation, useQuery } from "@apollo/client";
import { LinkProps } from "@generouted/react-router/client";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Controller, useController, useForm, type Control } from "react-hook-form";
import { useLocation } from "react-router-dom";

import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import ListItem from "@/components/ListItem";
import SectionHeading from "@/components/SectionHeading";
import TasksTable from "@/features/global/components/TasksTable";
import { PERSON_DETAILS_HEADER_CAREER_QUERY, PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { dateValueTypeToISOString } from "@/features/global/components/NotesTable/noteHelpers";
import { DOMAIN, PERMISSION, useCanManageTasksBool, useHasCapabilityBool } from "@/hooks/useCapabilities";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import useCurrentUser from "@/hooks/useCurrentUser";
import useSpeechToText from "@/hooks/useSpeechToText";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { toFormat } from "@/lib/dateHelpers";
import { dictationErrorMessage, TALK_IT_OUT_UNSUPPORTED_MESSAGE } from "@/lib/dictation";
import { renderName } from "@/lib/personHelpers";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import ErrorBannerProvider, { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { useNavigate, type Params } from "@/router";
import type { Person } from "@/types";

import ActionPlanRows from "./_ActionPlanRows";
import AutoFocusedPlanField from "./_AutoFocusedPlanField";
import CareerPlanAttachTaskSection from "./_CareerPlanAttachTaskSection";
import CareerPlanEmptySection, { CAREER_PLAN_SECTION_ADD_TEST_IDS } from "./_CareerPlanEmptySection";
import CareerPlanStatusDialog, { type CareerPlanStatusDialogMode } from "./_CareerPlanStatusDialog";
import CareerPlanTitle from "./_CareerPlanTitle";
import HeadlineDescriptionList from "./_HeadlineDescriptionList";
import TalkItOutButton from "./_TalkItOutButton";
import { useDownloadCareerPlanPdf } from "./_useDownloadCareerPlanPdf";
import {
  buildCareerPlanFollowUpTaskInput,
  buildCreateCareerPlanInput,
  buildUpdateCareerPlanInput,
  careerPlanFollowUpDueDate,
  CAREER_PLAN_INTRO,
  currentPositionLabel,
  areCareerPlanFormValuesEqual,
  emptyCareerPlanFormValues,
  formValuesFromCareerPlan,
  isCareerPlanDocumentPublishable,
  isCompletedCareerPlan,
  pruneCareerPlanFormValues,
  pruneEmptyCareerPlanCards,
  snapshotCareerPlanSection,
  type CareerPlanDocumentFormValues,
  type CareerPlanSection
} from "./careerPlanDocument";
import {
  CAREER_PLAN_STATUS_SNACKBAR,
  careerPlanStatusForDialogMode,
  getCareerPlanDocumentClusterActions
} from "./careerPlanStatusActions";
import {
  CREATE_CAREER_PLAN_MUTATION,
  DELETE_CAREER_PLAN_MUTATION,
  UPDATE_CAREER_PLAN_MUTATION,
  UPDATE_CAREER_PLAN_STATUS_MUTATION,
  type CareerPlanMutationResult,
  type GeneratedCareerPlan
} from "./mutations";
import { GET_CAREER_PLAN_HUB_QUERY } from "../queries";
import { GET_CAREER_PLAN_LINKED_TASKS_QUERY, GET_PERSON_CAREER_PLANS_QUERY } from "./queries";

type CareerPlanDocumentFormProps = {
  pgaId: string;
  planId?: string;
  canManage: boolean;
  backLink: LinkProps<"/people/:pgaId/career/career-profile", Params>;
};

type PersonData = { person: Person };

export default function CareerPlanDocumentForm(props: CareerPlanDocumentFormProps) {
  return (
    <ErrorBannerProvider>
      <CareerPlanDocumentEditor {...props} />
    </ErrorBannerProvider>
  );
}

function CareerPlanDocumentEditor({ pgaId, planId, canManage, backLink }: CareerPlanDocumentFormProps) {
  const { user } = useCurrentUser();
  const currentBackofficeUser = useCurrentBackofficeUser();
  const canManageTasks = useCanManageTasksBool();
  const navigate = useNavigate();
  const location = useLocation();
  const { setError, clearError } = useErrorBanner();
  const generatedPlanValues = planId ? null : unsavedGeneratedPlanValues(location.state);
  const { control, reset, watch, getValues } = useForm<CareerPlanDocumentFormValues>({
    defaultValues: generatedPlanValues ?? emptyCareerPlanFormValues()
  });
  const formValues = watch();
  const status = watch("status");
  const careerTarget = watch("careerTarget");
  const additionalNotes = watch("additionalNotes");
  const strengths = watch("strengths");
  const focusAreas = watch("focusAreas");
  const actionItems = watch("actionItems");
  const [editingSection, setEditingSection] = useState<CareerPlanSection | null>(null);
  const [savedPlanReady, setSavedPlanReady] = useState(!planId);
  const [saving, setSaving] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [attachTask, setAttachTask] = useState(true);
  const [taskDueDate, setTaskDueDate] = useState(() => careerPlanFollowUpDueDate());
  const [statusDialog, setStatusDialog] = useState<CareerPlanStatusDialogMode | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const hydratedPlanId = useRef<string | null>(null);
  const createdPlanIdRef = useRef<string | null>(null);
  const sectionSnapshot = useRef<Partial<CareerPlanDocumentFormValues> | null>(null);
  const savedValues = useRef(emptyCareerPlanFormValues());
  const savingRef = useRef(false);
  const canEdit = canManage && !isCompletedCareerPlan(status) && savedPlanReady;
  const [createCareerPlan] = useMutation(CREATE_CAREER_PLAN_MUTATION);
  const [updateCareerPlan] = useMutation(UPDATE_CAREER_PLAN_MUTATION);
  const [updateCareerPlanStatus] = useMutation(UPDATE_CAREER_PLAN_STATUS_MUTATION);
  const [deleteCareerPlan] = useMutation(DELETE_CAREER_PLAN_MUTATION);
  const { downloadPdf, downloadingPlanId } = useDownloadCareerPlanPdf(pgaId);
  const { data: linkedTasksData, loading: linkedTasksLoading } = useQuery(GET_CAREER_PLAN_LINKED_TASKS_QUERY, {
    skip: !planId || !canManageTasks,
    variables: {
      first: 1,
      input: { referenceableId: planId ?? "", referenceableType: ["CAREER_PLAN"] }
    },
    fetchPolicy: "network-only"
  });
  const hasAssociatedTask = (linkedTasksData?.tasks?.nodes ?? []).some((node) => Boolean(node?.id));
  const linkedTasksReady = !planId || !canManageTasks || !linkedTasksLoading;

  const beginEdit = (section: CareerPlanSection) => {
    if (saving) return;
    if (editingSection === section) return;
    if (editingSection != null) return;
    sectionSnapshot.current = snapshotCareerPlanSection(section, getValues());
    setEditingSection(section);
  };

  const closeEdit = () => {
    const section = editingSection;
    sectionSnapshot.current = null;
    setEditingSection(null);
    restoreSectionTriggerFocus(section);
  };

  const finishEdit = () => {
    if (editingSection === "strengths" || editingSection === "focusAreas" || editingSection === "actionPlan") {
      reset({ ...getValues(), ...pruneEmptyCareerPlanCards(editingSection, getValues()) });
    }
    closeEdit();
  };

  const cancelEdit = () => {
    if (sectionSnapshot.current) {
      reset({ ...getValues(), ...sectionSnapshot.current });
    }
    closeEdit();
  };

  const leaveToHub = () => {
    navigate("/people/:pgaId/career/career-profile", { params: { pgaId } });
  };

  const handlePageCancel = () => {
    if (saving) return;
    if (areCareerPlanFormValuesEqual(getValues(), savedValues.current)) {
      leaveToHub();
      return;
    }
    setShowDiscardDialog(true);
  };

  const persistUnsavedContent = async (): Promise<boolean> => {
    if (!planId) {
      setStatusError("Unable to update Career Plan status");
      return false;
    }

    const values = pruneCareerPlanFormValues(getValues());
    const followUpTask = getFollowUpTaskInput(values.title);
    if (areCareerPlanFormValuesEqual(values, pruneCareerPlanFormValues(savedValues.current)) && !followUpTask) {
      return true;
    }

    const mutationResult = await updateCareerPlan({
      variables: { input: buildUpdateCareerPlanInput(planId, values, followUpTask) }
    });
    const mutationErrors = mutationResult.errors;
    const payload = mutationResult.data?.updateCareerPlan;

    if (mutationErrors?.length) {
      setStatusError(mutationErrors[0]?.message || "Unable to save Career Plan");
      return false;
    }

    if (!payload?.success) {
      setStatusError(payload?.message || "Unable to save Career Plan");
      return false;
    }

    if (payload.taskError) {
      showErrorSnackbar(payload.taskError);
    }

    reset(values);
    savedValues.current = values;
    sectionSnapshot.current = null;
    setEditingSection(null);
    return true;
  };

  const openStatusDialog = (mode: CareerPlanStatusDialogMode) => {
    if (saving) return;
    setStatusError(null);
    setStatusDialog(mode);
  };

  const closeStatusDialog = () => {
    if (saving) return;
    setStatusDialog(null);
    setStatusError(null);
  };

  const handleStatusConfirm = async () => {
    if (!statusDialog || savingRef.current) return;
    if (statusDialog === "delete" && !planId) return;

    savingRef.current = true;
    setStatusError(null);
    clearError();
    setSaving(true);

    try {
      const planRefetchQueries = [
        { query: GET_PERSON_CAREER_PLANS_QUERY, variables: { pgaId } },
        { query: GET_CAREER_PLAN_HUB_QUERY, variables: { pgaId } }
      ];

      if (statusDialog === "delete") {
        const mutationResult = await deleteCareerPlan({
          variables: { id: planId! },
          refetchQueries: planRefetchQueries,
          awaitRefetchQueries: true
        });
        const mutationErrors = mutationResult.errors;
        const payload = mutationResult.data?.deleteCareerPlan;

        if (mutationErrors?.length) {
          setStatusError(mutationErrors[0]?.message || "Unable to delete Career Plan");
          return;
        }

        if (!payload?.success) {
          setStatusError(payload?.message || "Unable to delete Career Plan");
          return;
        }
      } else {
        let effectivePlanId = planId ?? createdPlanIdRef.current;

        if (!effectivePlanId) {
          const values = pruneCareerPlanFormValues(getValues());
          const followUpTask = getFollowUpTaskInput(values.title);
          const createResult = await createCareerPlan({
            variables: { input: buildCreateCareerPlanInput(pgaId, values, followUpTask) },
            refetchQueries: planRefetchQueries,
            awaitRefetchQueries: true
          });
          const createErrors = createResult.errors;
          const createPayload = createResult.data?.createCareerPlan;

          if (createErrors?.length) {
            setStatusError(createErrors[0]?.message || "Unable to save Career Plan");
            return;
          }

          if (!createPayload?.success || !createPayload.careerPlan?.id) {
            setStatusError(createPayload?.message || "Unable to save Career Plan");
            return;
          }

          if (createPayload.taskError) {
            showErrorSnackbar(createPayload.taskError);
          }

          createdPlanIdRef.current = createPayload.careerPlan.id;
          effectivePlanId = createPayload.careerPlan.id;
        } else if (planId) {
          const persisted = await persistUnsavedContent();
          if (!persisted) return;
        }

        const mutationResult = await updateCareerPlanStatus({
          variables: { id: effectivePlanId, status: careerPlanStatusForDialogMode(statusDialog) },
          refetchQueries: planRefetchQueries,
          awaitRefetchQueries: true
        });
        const mutationErrors = mutationResult.errors;
        const payload = mutationResult.data?.updateCareerPlanStatus;

        if (mutationErrors?.length) {
          setStatusError(mutationErrors[0]?.message || "Unable to update Career Plan status");
          return;
        }

        if (!payload?.success) {
          setStatusError(payload?.message || "Unable to update Career Plan status");
          return;
        }
      }

      setStatusDialog(null);
      showSnackbar(CAREER_PLAN_STATUS_SNACKBAR[statusDialog]);
      leaveToHub();
    } catch (statusMutationError) {
      setStatusError(
        statusMutationError instanceof Error
          ? statusMutationError.message
          : statusDialog === "delete"
            ? "Unable to delete Career Plan"
            : "Unable to update Career Plan status"
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit || savingRef.current) return;

    savingRef.current = true;
    clearError();
    const values = pruneCareerPlanFormValues(getValues());
    setSaving(true);

    try {
      const refetchQueries = [{ query: GET_PERSON_CAREER_PLANS_QUERY, variables: { pgaId } }];
      let payload: CareerPlanMutationResult | undefined;
      let mutationErrors: readonly { message: string }[] | undefined;

      const followUpTask = getFollowUpTaskInput(values.title);

      if (planId) {
        const mutationResult = await updateCareerPlan({
          variables: { input: buildUpdateCareerPlanInput(planId, values, followUpTask) },
          refetchQueries
        });
        mutationErrors = mutationResult.errors;
        payload = mutationResult.data?.updateCareerPlan;
      } else {
        const mutationResult = await createCareerPlan({
          variables: { input: buildCreateCareerPlanInput(pgaId, values, followUpTask) },
          refetchQueries
        });
        mutationErrors = mutationResult.errors;
        payload = mutationResult.data?.createCareerPlan;
      }

      if (mutationErrors?.length) {
        setError(mutationErrors[0]?.message || "Unable to save Career Plan");
        return;
      }

      if (!payload?.success) {
        setError(payload?.message || "Unable to save Career Plan");
        return;
      }

      reset(values);
      sectionSnapshot.current = null;
      setEditingSection(null);
      showSnackbar("Career Plan saved");
      if (payload.taskError) {
        showErrorSnackbar(payload.taskError);
      }
      leaveToHub();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save Career Plan");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });

  const { data: headerData } = useQuery<PersonData>(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId }
  });
  const { data: careerData } = useQuery<PersonData>(PERSON_DETAILS_HEADER_CAREER_QUERY, {
    variables: { pgaId },
    skip: !canViewPersonCareer
  });
  const {
    data: plansData,
    error: plansError,
    loading: plansLoading
  } = useQuery(GET_PERSON_CAREER_PLANS_QUERY, {
    variables: { pgaId },
    skip: !planId,
    fetchPolicy: "cache-first"
  });
  const person = headerData?.person
    ? {
        ...headerData.person,
        ...(careerData?.person?.employments !== undefined ? { employments: careerData.person.employments } : {})
      }
    : undefined;
  const memberName = person?.displayName
    ? renderName(
        person.displayName,
        person.informalName ?? undefined,
        person.preferredLastName ?? undefined,
        person.suffixName ?? undefined,
        person.middleName ?? undefined,
        person.firstName ?? undefined
      )
    : "";
  const position = currentPositionLabel(person?.employments ?? []);
  const getFollowUpTaskInput = (title: string) =>
    attachTask && canManageTasks && linkedTasksReady && !hasAssociatedTask
      ? buildCareerPlanFollowUpTaskInput({
          title,
          memberName,
          assignedToId: currentBackofficeUser?.id,
          dueDate: dateValueTypeToISOString(taskDueDate)
        })
      : null;
  const savedPlan = plansData?.person?.careerPlans?.find((plan) => plan.id === planId);
  const preparedBy = savedPlan?.preparedByName || user?.name || "";
  const lastModified = savedPlan?.updatedAt ? toFormat(savedPlan.updatedAt) : null;

  useEffect(() => {
    if (!planId) {
      setSavedPlanReady(true);
      return;
    }
    if (plansLoading || hydratedPlanId.current === planId) return;
    if (plansError) return;
    if (plansData === undefined) return;
    hydratedPlanId.current = planId;
    const plan = plansData.person?.careerPlans?.find((item) => item.id === planId);
    if (plan) {
      const values = formValuesFromCareerPlan(plan);
      reset(values);
      savedValues.current = values;
    }
    sectionSnapshot.current = null;
    setEditingSection(null);
    setSavedPlanReady(true);
  }, [planId, plansData, plansError, plansLoading, reset]);

  if (planId && !plansLoading && plansError) {
    return (
      <FocusedPageLayout backLink={backLink} stickyActions>
        <div role="alert">Error: {plansError.message}</div>
      </FocusedPageLayout>
    );
  }

  const sectionActions = (section: CareerPlanSection, hasContent = true) => {
    if (!canEdit || editingSection === section) return [];
    if (!hasContent) return [];
    return [
      {
        label: "Edit",
        variant: "outlined" as const,
        onClick: () => beginEdit(section),
        disabled: editingSection != null || saving,
        testId: `career-plan-edit-${section}`
      }
    ];
  };

  const isEditing = (section: CareerPlanSection) => canEdit && editingSection === section;
  const isSectionInactive = (section: CareerPlanSection) => editingSection != null && editingSection !== section;
  const showAdditionalNotesSection = canEdit || additionalNotes.trim().length > 0;
  const canChangeStatus = canEdit && (Boolean(savedPlan) || isCareerPlanDocumentPublishable(formValues));
  const canDelete = canManage && Boolean(savedPlan) && savedPlanReady;
  const canDownload = Boolean(planId) && savedPlanReady && Boolean(savedPlan);

  const clusterActions = getCareerPlanDocumentClusterActions({
    status,
    canEdit,
    canDelete,
    canDownload,
    canChangeStatus,
    saving,
    downloadingPdf: downloadingPlanId === planId,
    statusDialogOpen: statusDialog != null,
    onSave: handleSave,
    onDownloadPdf: () => {
      if (planId) void downloadPdf(planId);
    },
    handlers: {
      publish: () => openStatusDialog("publish"),
      unpublish: () => openStatusDialog("unpublish"),
      complete: () => openStatusDialog("complete"),
      delete: () => openStatusDialog("delete")
    }
  });

  return (
    <FocusedPageLayout backLink={backLink} onBackClick={handlePageCancel} clusterActions={clusterActions} stickyActions>
      {showDiscardDialog && (
        <DiscardCareerPlanChangesDialog
          open={showDiscardDialog}
          onDismiss={() => setShowDiscardDialog(false)}
          onDiscard={leaveToHub}
        />
      )}
      {statusDialog && (
        <CareerPlanStatusDialog
          open
          mode={statusDialog}
          saving={saving}
          error={statusError}
          onClose={closeStatusDialog}
          onConfirm={() => {
            void handleStatusConfirm();
          }}
        />
      )}
      <form
        data-testid="career-plan-document-page"
        onSubmit={(event) => event.preventDefault()}
        ref={(node) => {
          node?.toggleAttribute("inert", saving);
        }}
      >
        <div className="pt-8 min-w-0">
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <CareerPlanTitle
                title={field.value}
                onTitleChange={canEdit && !saving ? field.onChange : undefined}
                disabled={editingSection != null || saving}
              />
            )}
          />
          <p className="mt-3 text-body-medium text-sys-brand-on-surface-variant" data-testid="career-plan-intro">
            {CAREER_PLAN_INTRO}
          </p>
        </div>
        <PlanSection section="details" inactive={isSectionInactive("details")}>
          <SectionHeading
            heading="Overview"
            body={lastModified ? `Last modified ${lastModified}` : undefined}
            actions={sectionActions("details")}
          />
          {isEditing("details") ? (
            <div className="-mt-2 w-full" data-testid="career-plan-details-edit">
              <AutoFocusedPlanField control={control} name="timeframe" label="Timeframe" />
              <SectionEditActions section="details" onDone={finishEdit} onCancel={cancelEdit} />
            </div>
          ) : (
            <div className="-mt-2 grid grid-cols-1 gap-4 md:grid-cols-4" data-testid="career-plan-details">
              <DetailListItem iconName="person" overline="Member Name" headline={memberName} />
              <DetailListItem iconName="business_center" overline="Current Position" headline={position} />
              <DetailListItem iconName="badge" overline="Prepared By" headline={preparedBy} />
              <Controller
                control={control}
                name="timeframe"
                render={({ field }) => (
                  <DetailListItem iconName="schedule" overline="Timeframe" headline={field.value} />
                )}
              />
            </div>
          )}
        </PlanSection>

        <PlanSection section="careerTarget" inactive={isSectionInactive("careerTarget")}>
          <SectionHeading
            heading="Career Target"
            actions={sectionActions("careerTarget", careerTarget.trim().length > 0)}
          />
          {isEditing("careerTarget") ? (
            <div className="w-full" data-testid="career-plan-careerTarget-edit">
              <AutoFocusedPlanField
                control={control}
                name="careerTarget"
                label="Career Target"
                type="textarea"
                rows={4}
              />
              <SectionEditActions section="careerTarget" onDone={finishEdit} onCancel={cancelEdit} />
            </div>
          ) : careerTarget.trim() ? (
            <PlanSectionBody value={careerTarget} testId="career-plan-careerTarget" />
          ) : (
            <CareerPlanEmptySection
              section="careerTarget"
              title="No Career Target Added"
              addLabel="Add a Career Target"
              canManage={canEdit}
              disabled={isSectionInactive("careerTarget")}
              onAdd={() => beginEdit("careerTarget")}
            />
          )}
        </PlanSection>

        <PlanSection section="strengths" inactive={isSectionInactive("strengths")}>
          <SectionHeading heading="Strengths You Bring" actions={sectionActions("strengths", strengths.length > 0)} />
          <HeadlineDescriptionList
            control={control}
            name="strengths"
            addLabel="Add a Strength"
            noun="Strength"
            emptyTitle="No Strengths Added"
            readOnly={!isEditing("strengths")}
            canManage={canEdit}
            disabled={isSectionInactive("strengths")}
            onBeginEdit={() => beginEdit("strengths")}
          />
          {isEditing("strengths") && (
            <SectionEditActions section="strengths" onDone={finishEdit} onCancel={cancelEdit} />
          )}
        </PlanSection>

        <PlanSection section="focusAreas" inactive={isSectionInactive("focusAreas")}>
          <SectionHeading heading="Areas of Focus" actions={sectionActions("focusAreas", focusAreas.length > 0)} />
          <HeadlineDescriptionList
            control={control}
            name="focusAreas"
            addLabel="Add an Area of Focus"
            noun="Area of Focus"
            emptyTitle="No Areas of Focus Added"
            readOnly={!isEditing("focusAreas")}
            canManage={canEdit}
            disabled={isSectionInactive("focusAreas")}
            onBeginEdit={() => beginEdit("focusAreas")}
          />
          {isEditing("focusAreas") && (
            <SectionEditActions section="focusAreas" onDone={finishEdit} onCancel={cancelEdit} />
          )}
        </PlanSection>

        <PlanSection section="actionPlan" inactive={isSectionInactive("actionPlan")}>
          <SectionHeading heading="Action Plan" actions={sectionActions("actionPlan", actionItems.length > 0)} />
          <ActionPlanRows
            control={control}
            emptyTitle="No Action Plan Rows Added"
            addLabel="Add a Row"
            readOnly={!isEditing("actionPlan")}
            canManage={canEdit}
            disabled={isSectionInactive("actionPlan")}
            onBeginEdit={() => beginEdit("actionPlan")}
          />
          {isEditing("actionPlan") && (
            <SectionEditActions section="actionPlan" onDone={finishEdit} onCancel={cancelEdit} />
          )}
        </PlanSection>

        {showAdditionalNotesSection && (
          <PlanSection section="additionalNotes" inactive={isSectionInactive("additionalNotes")}>
            <div data-testid="career-plan-additional-notes">
              <SectionHeading
                heading="Notes from Your Consultant"
                actions={sectionActions("additionalNotes", additionalNotes.trim().length > 0)}
              />
              {isEditing("additionalNotes") ? (
                <div className="w-full" data-testid="career-plan-additionalNotes-edit">
                  <AdditionalNotesEdit control={control} />
                  <SectionEditActions section="additionalNotes" onDone={finishEdit} onCancel={cancelEdit} />
                </div>
              ) : additionalNotes.trim() ? (
                <PlanSectionBody value={additionalNotes} testId="career-plan-additionalNotes" />
              ) : (
                <CareerPlanEmptySection
                  section="additionalNotes"
                  title="No Consultant Notes Added"
                  addLabel="Add a Note"
                  canManage={canEdit}
                  disabled={isSectionInactive("additionalNotes")}
                  onAdd={() => beginEdit("additionalNotes")}
                />
              )}
            </div>
          </PlanSection>
        )}
        {canManageTasks && canEdit && !hasAssociatedTask && linkedTasksReady ? (
          <CareerPlanAttachTaskSection
            attachTask={attachTask}
            dueDate={taskDueDate}
            disabled={saving}
            onToggle={setAttachTask}
            onDueDateChange={setTaskDueDate}
          />
        ) : null}
        {canManageTasks && hasAssociatedTask ? (
          <TasksTable
            referenceableId={planId ?? ""}
            referenceableType="CAREER_PLAN"
            tableHeading="Career Plan Tasks"
            tableBody="View and manage tasks for this Career Plan. Tasks are not visible to the member."
            hideControls
            defaultPrivate
            skipTeamAutoTag
          />
        ) : null}
      </form>
    </FocusedPageLayout>
  );
}

function AdditionalNotesEdit({ control }: { control: Control<CareerPlanDocumentFormValues> }) {
  const { field } = useController({ control, name: "additionalNotes" });
  const { listening, toggle } = useSpeechToText({
    value: field.value,
    onChange: field.onChange,
    onUnsupported: () => showSnackbar(TALK_IT_OUT_UNSUPPORTED_MESSAGE),
    onError: (error) => {
      const message = dictationErrorMessage(error);
      if (message) showErrorSnackbar(message);
    }
  });

  return (
    <div className="flex flex-col gap-4">
      <AutoFocusedPlanField
        control={control}
        name="additionalNotes"
        label="Notes from Your Consultant"
        type="textarea"
        rows={4}
        disabled={listening}
      />
      <TalkItOutButton listening={listening} onClick={toggle} testId="career-plan-additionalNotes-talk-it-out" />
    </div>
  );
}

function SectionEditActions({
  section,
  onDone,
  onCancel
}: {
  section: CareerPlanSection;
  onDone: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mt-4 flex flex-row gap-2">
      <Button type="button" onClick={onDone} testId={`career-plan-done-${section}`}>
        Done
      </Button>
      <Button type="button" variant="text" onClick={onCancel} testId={`career-plan-cancel-${section}`}>
        Cancel
      </Button>
    </div>
  );
}

function DiscardCareerPlanChangesDialog({
  open,
  onDismiss,
  onDiscard
}: {
  open: boolean;
  onDismiss: () => void;
  onDiscard: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClosed={onDismiss}
      headline="Discard Changes"
      className="sm:max-w-[423px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={onDismiss} testId="keep-editing-career-plan">
            Keep Editing
          </Button>
          <Button variant="filled" onClick={onDiscard} testId="discard-career-plan-changes">
            Discard Changes
          </Button>
        </>
      }
    >
      <p>Do you want to discard your changes to this career plan? This cannot be undone.</p>
    </Dialog>
  );
}

function PlanSectionBody({ value, testId }: { value: string; testId: string }) {
  return (
    <p className="text-body-medium text-sys-brand-on-surface whitespace-pre-wrap" data-testid={testId}>
      {value}
    </p>
  );
}

function restoreSectionTriggerFocus(section: CareerPlanSection | null) {
  if (!section) return;

  requestAnimationFrame(() => {
    const edit = document.querySelector<HTMLElement>(`[data-testid="career-plan-edit-${section}"]`);
    if (edit) {
      edit.focus();
      return;
    }

    if (section === "details") return;
    document.querySelector<HTMLElement>(`[data-testid="${CAREER_PLAN_SECTION_ADD_TEST_IDS[section]}"]`)?.focus();
  });
}

function PlanSection({
  section,
  inactive,
  children
}: {
  section: CareerPlanSection;
  inactive: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={inactive ? "opacity-50" : undefined}
      ref={(node) => {
        node?.toggleAttribute("inert", inactive);
      }}
      data-testid={`career-plan-section-${section}`}
    >
      {children}
    </div>
  );
}

function unsavedGeneratedPlanValues(locationState: unknown): CareerPlanDocumentFormValues | null {
  if (locationState == null || typeof locationState !== "object" || !("generatedPlan" in locationState)) {
    return null;
  }
  const generatedPlan = (locationState as { generatedPlan?: GeneratedCareerPlan | null }).generatedPlan;
  if (generatedPlan == null) return null;
  return formValuesFromCareerPlan({
    ...generatedPlan,
    additionalNotes: ""
  });
}

function DetailListItem({
  iconName,
  overline,
  headline
}: {
  iconName: "person" | "business_center" | "badge" | "schedule";
  overline: string;
  headline: string;
}) {
  return (
    <ListItem iconName={iconName} iconBackground overline={overline} headline={headline.trim() ? headline : "--"} />
  );
}
